import {
  REPARTITION_CA_DATA,
  RepartitionCaDetail,
} from "../../../dashboard/models/data.model";
import { StatsArticle } from "../../models/stats-article.model";
import { parseNumber } from "../../../../shared/utils/number-utils.service";
import type { ArticlesInfosFacadeService } from "../../services/article-infos/article-infos-facade.service";

/**
 * Transforme `StatsArticle` (facturesLignes + ofs) en `REPARTITION_CA_DATA`,
 * version article du `repartition-ca-from-stats.util.ts` côté tiers.
 *
 * - Le coût lot par u est dérivé des OFs : `(coutMatTotalReel + coutPersoTotalReel) / quantiteProduite`
 *   indexé par `lotId` (premier lot de l'OF).
 * - Le tiers du détail est extrait de la facture.
 * - `tiersGroupe` reprend la première dénomination de `tier.tierGroups` ou
 *   "Sans groupe" si vide → permet le tri par groupe avec niveau article
 *   intercalé géré par `repartition-ca-dialog`.
 */
export function repartitionCaFromStatsArticle(
  stats: StatsArticle | null | undefined,
  articleInfosFacade: ArticlesInfosFacadeService,
): REPARTITION_CA_DATA | null {
  if (!stats?.facturesLignes?.length) return null;

  // Index lotId → coût mat/MO unitaire (en €/u base) calculé depuis les OFs.
  // On ne prend que les valeurs réelles si dispo, sinon les théoriques.
  const lotCostMap = new Map<string, { mat: number; mo: number }>();
  for (const ofr of (stats.ofs ?? []) as any[]) {
    const lotId: string | undefined = ofr?.lots?.[0]?.id;
    if (!lotId) continue;
    const q = parseNumber(ofr.quantiteProduite || 0);
    if (q <= 0) continue;
    const mat = parseNumber(
      ofr.coutMatiereTotalReel ?? ofr.coutMatiereTotalTheorique ?? 0,
    );
    const mo = parseNumber(
      ofr.coutPersonnelTotalReel ?? ofr.coutPersonnelTotalTheorique ?? 0,
    );
    lotCostMap.set(lotId, { mat: mat / q, mo: mo / q });
  }

  const details: RepartitionCaDetail[] = [];
  const clientMap = new Map<string, number>();
  const articleMap = new Map<string, number>();
  const familleMap = new Map<string, number>();
  const groupeTiersMap = new Map<string, number>();
  let total = 0;

  for (const fl of stats.facturesLignes as any[]) {
    const f = fl?.facture;
    const t = f?.tiers;
    const tiersDenomination: string =
      t?.denomination ?? f?.tiersDenomination ?? "Client inconnu";
    const tiersGroupe: string =
      t?.tierGroups?.[0]?.denomination ?? "Sans groupe";

    const articleId: string =
      fl?.articleId ??
      fl?.article?.id ??
      fl?.commandeClientLigne?.articleId ??
      fl?.commandeClientLigne?.article?.id ??
      "Inconnu";
    const ai = articleInfosFacade.getByArticleId(articleId)?.();
    const articleName: string = ai?.denomination ?? "Sans article";
    const familleName: string =
      (ai?.famille as any)?.denomination ?? "Non classé";
    const uniteDenom: string = (ai?.unite as any)?.denomination ?? "u";

    const coefUG =
      parseNumber(fl?.commandeClientLigne?.coefficientUniteGestion ?? 1) || 1;
    const qty = parseNumber(fl?.quantite ?? 0);
    const pu = parseNumber(fl?.prix ?? 0);
    const remise = parseNumber(fl?.remise ?? 0);
    const montantBrut = qty * pu;
    const montantHT = montantBrut - remise;
    if (montantHT <= 0) continue;

    const qtyUG = qty * coefUG;
    const lotId: string | undefined = fl?.lotId ?? fl?.lot?.id;
    const lotCost = lotId ? lotCostMap.get(lotId) : null;
    const coutMateriel = qtyUG * (lotCost?.mat ?? 0);
    const coutMO = qtyUG * (lotCost?.mo ?? 0);
    const coutUnitaire = (lotCost?.mat ?? 0) + (lotCost?.mo ?? 0);

    const lotNumero: string = fl?.lot?.numero ?? "";
    const rawDate: any = f?.date ?? fl?.date ?? null;
    const dateStr: string = rawDate ? new Date(rawDate).toISOString() : "";

    total += montantHT;
    clientMap.set(
      tiersDenomination,
      (clientMap.get(tiersDenomination) ?? 0) + montantHT,
    );
    articleMap.set(articleName, (articleMap.get(articleName) ?? 0) + montantHT);
    familleMap.set(familleName, (familleMap.get(familleName) ?? 0) + montantHT);
    groupeTiersMap.set(
      tiersGroupe,
      (groupeTiersMap.get(tiersGroupe) ?? 0) + montantHT,
    );

    details.push({
      tiersDenomination,
      tiersGroupe,
      articleDenomination: articleName,
      familleDenomination: familleName,
      date: dateStr,
      lotId: lotId ?? undefined,
      lotNumero,
      quantite: qty,
      quantiteUG: qtyUG,
      prixUnitaire: pu,
      coutUnitaire,
      uniteVente: uniteDenom,
      uniteGestion: uniteDenom,
      montantHT,
      montantBrut,
      coutMateriel,
      coutMO,
    });
  }

  const toArray = (m: Map<string, number>) =>
    [...m.entries()].map(([denomination, t]) => ({ denomination, total: t }));

  return {
    byClient: toArray(clientMap),
    byArticle: toArray(articleMap),
    byFamille: toArray(familleMap),
    byGroupeTiers: toArray(groupeTiersMap),
    details,
    total,
  };
}
